import {useEffect, useRef, useState} from "react";
import {PageModel} from "@/api/Paging.ts";

type SearchState<T> = {
    requestId: string;
    results?: PageModel<T>;
    error: boolean;
};

export default function useAsyncSearch<T>({query, search, minQueryLength = 2, debounceMs = 150, requestKey = ""}: {
    query: string;
    search: (query: string, signal: AbortSignal) => Promise<PageModel<T>>;
    minQueryLength?: number;
    debounceMs?: number;
    requestKey?: string | number;
}) {
    const normalizedQuery = query.trim();
    const enabled = normalizedQuery.length >= minQueryLength;
    const requestId = `${requestKey}:${normalizedQuery}`;
    const searchRef = useRef(search);
    const [state, setState] = useState<SearchState<T>>({requestId: "", error: false});

    useEffect(() => {
        searchRef.current = search;
    }, [search]);

    useEffect(() => {
        if (!enabled) return;

        const controller = new AbortController();
        const timeout = window.setTimeout(() => {
            void searchRef.current(normalizedQuery, controller.signal)
                .then(results => setState({requestId, results, error: false}))
                .catch(() => {
                    if (!controller.signal.aborted) setState({requestId, error: true});
                });
        }, debounceMs);

        return () => {
            window.clearTimeout(timeout);
            controller.abort();
        };
    }, [debounceMs, enabled, normalizedQuery, requestId]);

    const current = state.requestId === requestId;
    return {
        enabled,
        results: enabled && current ? state.results : undefined,
        loading: enabled && !current,
        error: enabled && current && state.error,
    };
}
